import { Link } from 'react-router-dom';
import { ArrowUpRight, Lock } from 'lucide-react';
import PageShell from '@/components/PageShell';
import SectionHeader from '@/components/SectionHeader';
import { VENTURES } from '@/lib/platforms';

const INVESTMENTS_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'The Mobility Group · Investment Portfolio',
  description:
    'Operating and backed ventures across the automotive and mobility ecosystem — commercial vehicle marketplaces, market intelligence, neutral data exchanges and active scouting in EV, telematics and aftermarket.',
  url: 'https://themobilitygroup.co/investments',
  itemListElement: VENTURES.filter((v) => v.url).map((v, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    item: {
      '@type': 'Organization',
      name: v.name,
      url: v.url,
      description: v.summary,
      industry: v.sector,
    },
  })),
};

const heroImg =
  'https://images.unsplash.com/photo-1670509295484-df0c2512fec4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHx0cnVjayUyMGhpZ2h3YXklMjBkYXJrfGVufDB8fHx8MTc4MTc5MjE4Mnww&ixlib=rb-4.1.0&q=85';

const Investments = () => {
  return (
    <PageShell>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(INVESTMENTS_JSONLD) }}
      />
      <section className="relative border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImg} alt="trucks" className="w-full h-full object-cover opacity-35" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/80 to-black/40" />
        </div>
        <div className="relative px-6 md:px-12 lg:px-16 pt-24 pb-16">
          <div className="overline text-amber mb-4">
            <span className="inline-flex items-center gap-2">
              <span className="h-px w-6 bg-amber inline-block" />
              Investments
            </span>
          </div>
          <h1 className="font-display text-[40px] sm:text-5xl md:text-6xl lg:text-7xl text-balance max-w-4xl leading-[1.02]">
            Capital, distribution, and operating muscle — for automotive ventures.
          </h1>
          <p className="mt-8 max-w-2xl text-zinc-300 text-base md:text-lg leading-relaxed">
            We back founders building in the automotive and mobility sector —
            commercial vehicles, EVs, data, financing, after-sales, and
            workshop-grade software. Our cheques come with platforms.
          </p>
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-16 py-20 md:py-28">
        <SectionHeader
          kicker="Portfolio & operating ventures"
          title="What we've built. What we back."
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {VENTURES.map((v) => (
            <div
              key={v.name}
              className="bg-ink-800 border border-white/10 p-8 md:p-10 hover:border-amber/40 transition-colors flex flex-col gap-5"
            >
              <div className="flex items-center justify-between">
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-cyan border border-cyan/30 px-2 py-1">
                  {v.sector}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                  {v.stake}
                </span>
              </div>
              <h3 className="font-display text-3xl md:text-4xl text-white">
                {v.name}
              </h3>
              <p className="text-zinc-400 leading-relaxed">{v.summary}</p>
              {v.url ? (
                <a
                  href={v.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-auto inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-amber hover:text-white transition-colors"
                >
                  Visit venture ↗
                </a>
              ) : v.name === 'Future bets' ? (
                <Link
                  to="/investments/future-bets"
                  className="mt-auto inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-amber hover:text-white transition-colors group"
                >
                  <Lock className="h-3 w-3" />
                  View pipeline · internal
                  <ArrowUpRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              ) : (
                <div className="mt-auto font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-500">
                  Pipeline · in conversation
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-16 py-20 md:py-28 bg-black border-y border-white/10">
        <SectionHeader
          kicker="Our thesis"
          title="Where we lean in."
          intro="We invest where data, distribution and operating depth give us — and our founders — an unfair edge."
        />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 border border-white/10">
          {[
            ['Commercial Vehicles', 'Trucks, buses, 3-wheelers, finance, used CV, telematics.'],
            ['EV & Charging', 'OEMs, charging networks, battery health, swapping economics.'],
            ['Dealer-tech', 'Showroom, workshop, parts and CRM software for dealer networks.'],
            ['Insurance & Lending', 'Underwriting, claims, asset finance and risk on real lifecycle data.'],
            ['Aftermarket & Workshops', 'Multi-brand service networks, parts commerce, mobile mechanics.'],
            ['Cross-border', 'Indian platforms going global; global ideas adapted for India.'],
          ].map(([t, d]) => (
            <div key={t} className="bg-ink-900 p-7">
              <h3 className="font-display text-xl mb-2">{t}</h3>
              <p className="text-zinc-400 text-sm leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-16 py-20 md:py-28 bg-ink-900">
        <div className="max-w-3xl">
          <h2 className="font-display text-3xl md:text-4xl mb-6">
            Building in automotive or mobility?
          </h2>
          <p className="text-zinc-400 leading-relaxed mb-8">
            We back early conviction. If your venture sits anywhere in the
            automotive value chain — talk to us before you talk to a generalist VC.
          </p>
          <Link
            to="/contact"
            className="group inline-flex items-center gap-2 border border-amber bg-amber px-6 py-3.5 text-xs font-mono uppercase tracking-[0.18em] text-black transition-colors hover:bg-white hover:border-white"
          >
            Pitch us
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </section>
    </PageShell>
  );
};

export default Investments;
